import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/lib/brand";
import api from "@/lib/api";
import { AuthShell, GoogleButton } from "@/components/AuthShell";
import { Checkbox } from "@/components/ui/checkbox";

const SKIN_TYPES = ["Dry", "Oily", "Combination", "Sensitive", "Normal", "Prefer not to say"];
const INTERESTS = ["Barrier care", "Sun protection", "Gentle cleansing", "Ingredient learning", "Routine building"];
const PRODUCTS = ["Serum", "Sunscreen", "Facewash"];

function Chip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-4 py-2 text-sm border transition-colors ${
        active ? "bg-forest text-ivory border-forest" : "border-forest/25 text-forest hover:bg-forest/5"
      }`}
    >
      {children}
    </button>
  );
}

function Onboarding({ user }) {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState(user.display_name || user.name || "");
  const [skinType, setSkinType] = useState("");
  const [interests, setInterests] = useState([]);
  const [prefs, setPrefs] = useState([]);
  const [account, setAccount] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [media, setMedia] = useState(false);
  const [saving, setSaving] = useState(false);

  const toggle = (list, setList, value) =>
    setList(list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);

  const submit = async (skip = false) => {
    if (!account) {
      toast.error("Please accept the account & participation agreement to continue.");
      return;
    }
    setSaving(true);
    try {
      const res = await api.put("/auth/profile", {
        display_name: displayName || null,
        skin_type: skip ? null : skinType || null,
        interests: skip ? [] : interests,
        product_preferences: skip ? [] : prefs,
        account_agreement: account,
        marketing_consent: marketing,
        media_consent: media,
      });
      setUser(res.data);
      toast.success(`Welcome to Rituals With You, ${res.data.first_name || "friend"}.`);
      navigate("/my-rituals", { replace: true });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full max-w-lg">
      <p className="eyebrow">{BRAND.descriptor}</p>
      <h1 className="mt-3 font-serif text-4xl text-forest">
        Welcome, {user.first_name || displayName || "friend"}
      </h1>
      <p className="mt-3 text-charcoal/70">
        A few optional details help us tailor your experience. You can skip and add these later.
      </p>

      <div className="mt-8 space-y-7">
        <div>
          <label className="text-sm font-medium text-forest">Display name</label>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            data-testid="onboard-display-name"
            className="mt-2 w-full border border-forest/20 bg-cream px-4 py-3 text-charcoal focus:border-forest outline-none"
            placeholder="How should the community know you?"
          />
        </div>

        <div>
          <label className="text-sm font-medium text-forest">Skin type <span className="text-charcoal/40 font-normal">(optional)</span></label>
          <div className="mt-2 flex flex-wrap gap-2">
            {SKIN_TYPES.map((s) => (
              <Chip key={s} active={skinType === s} onClick={() => setSkinType(skinType === s ? "" : s)}>{s}</Chip>
            ))}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-forest">Interests <span className="text-charcoal/40 font-normal">(optional)</span></label>
          <div className="mt-2 flex flex-wrap gap-2">
            {INTERESTS.map((s) => (
              <Chip key={s} active={interests.includes(s)} onClick={() => toggle(interests, setInterests, s)}>{s}</Chip>
            ))}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-forest">Products you're curious about <span className="text-charcoal/40 font-normal">(optional)</span></label>
          <div className="mt-2 flex flex-wrap gap-2">
            {PRODUCTS.map((s) => (
              <Chip key={s} active={prefs.includes(s)} onClick={() => toggle(prefs, setPrefs, s)}>{s}</Chip>
            ))}
          </div>
        </div>

        <div className="space-y-3 border border-forest/15 bg-cream p-5">
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox checked={account} onCheckedChange={setAccount} data-testid="consent-account" className="mt-0.5" />
            <span className="text-sm text-charcoal/80">
              I agree to the account &amp; participation terms. <span className="text-destructive">Required</span>
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox checked={marketing} onCheckedChange={setMarketing} data-testid="consent-marketing" className="mt-0.5" />
            <span className="text-sm text-charcoal/80">
              Send me occasional updates from ELA Rituals. <span className="text-charcoal/40">Optional</span>
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox checked={media} onCheckedChange={setMedia} data-testid="consent-media" className="mt-0.5" />
            <span className="text-sm text-charcoal/80">
              I may allow my consented stories to be published later. <span className="text-charcoal/40">Optional</span>
            </span>
          </label>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => submit(false)}
            disabled={saving}
            data-testid="onboard-submit"
            className="flex-1 inline-flex items-center justify-center gap-2 btn btn-primary disabled:opacity-60"
          >
            {saving ? "Saving…" : <>Enter My Rituals <Check size={16} /></>}
          </button>
          <button
            onClick={() => submit(true)}
            disabled={saving}
            className="btn btn-outline"
          >
            Skip details
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Join() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return <AuthShell><p className="font-serif text-2xl text-forest animate-pulse">One moment…</p></AuthShell>;
  }

  if (user && user.onboarded) {
    navigate("/my-rituals", { replace: true });
    return null;
  }

  if (user && !user.onboarded) {
    return <AuthShell><Onboarding user={user} /></AuthShell>;
  }

  return (
    <AuthShell>
      <div className="w-full max-w-md text-center">
        <p className="eyebrow">{BRAND.descriptor}</p>
        <h1 className="mt-4 font-serif text-4xl sm:text-5xl text-forest">You're invited to join</h1>
        <p className="mt-4 text-charcoal/70">
          Rituals With You is free to join. Discover our products, experience them when
          programmes open, and share your ritual with a community that listens.
        </p>
        <div className="mt-8">
          <GoogleButton next={new URLSearchParams(window.location.search).get("next") || "/my-rituals"} label="Join with Google" />
        </div>
        <p className="mt-5 text-xs text-charcoal/55 leading-relaxed">
          We use verified Google sign-in. Membership does not guarantee a product. You choose
          your permissions in the next step, and marketing is never on by default.
        </p>
        <p className="mt-6 text-sm text-charcoal/60">
          Already a member?{" "}
          <a href="/login" className="text-forest underline underline-offset-4">Sign in</a>
        </p>
      </div>
    </AuthShell>
  );
}
